import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { statusStyle } from "./Applications";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const INPUT = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm";

const Card = ({ title, children }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
    <h2 className="mb-4 text-lg font-bold text-slate-900">{title}</h2>
    {children}
  </section>
);
const Row = ({ label, children }) =>
  children ? (
    <div className="grid grid-cols-[170px_1fr] gap-3 py-1.5 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900">{children}</dd>
    </div>
  ) : null;
const addr = (a) => (a ? [a.street, a.city, a.postcode].filter(Boolean).join(", ") : "");
const contactLine = (c) => (c?.name ? `${c.name} · ${c.email || ""} · ${c.phone || ""}` : "");

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [notes, setNotes] = useState("");
  const [creditLimit, setCreditLimit] = useState("");
  const [approverTitle, setApproverTitle] = useState("");
  const [createDebtor, setCreateDebtor] = useState(true);

  const token = () => localStorage.getItem("token");
  const call = async (path, method = "GET", body) => {
    const res = await fetch(`${API_BASE}/applications/${id}${path}`, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { const e = new Error(data.message || "Request failed"); e.data = data; throw e; }
    return data;
  };

  const load = async () => {
    try {
      const d = await call("");
      setApp(d);
      setNotes(d.internalNotes || "");
      setCreditLimit(d.creditLimitApproved ?? d.creditLimitRequested ?? "");
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const run = async (fn) => {
    setBusy(true); setError(""); setMsg("");
    try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const saveNotes = (status) => run(async () => {
    await call("", "PATCH", { internalNotes: notes, creditLimitApproved: creditLimit === "" ? undefined : Number(creditLimit), ...(status ? { status } : {}) });
    setMsg("Saved."); await load();
  });

  const approve = (extra = {}) => run(async () => {
    if (!window.confirm(`Approve ${app.legalName}${createDebtor ? " and create the MoneyWorks debtor" : ""}?`)) return;
    try {
      const r = await call("/approve", "POST", {
        creditLimitApproved: creditLimit === "" ? undefined : Number(creditLimit),
        approverTitle, createDebtor, ...extra,
      });
      const mw = r.moneyworks;
      setMsg(
        mw?.ok ? `Approved. Customer created and MoneyWorks debtor ${mw.code} created.`
        : mw?.skipped ? "Approved. Customer created."
        : `Approved and customer created, but the MoneyWorks debtor failed: ${mw?.error}`
      );
      await load();
    } catch (e) {
      if (e.data?.existingCustomerId && window.confirm(`${e.message}\n\nOK = link to the existing customer.\nCancel = stop.`)) {
        await call("/approve", "POST", { existingCustomerId: e.data.existingCustomerId, creditLimitApproved: creditLimit === "" ? undefined : Number(creditLimit), approverTitle, createDebtor });
        setMsg("Approved and linked to existing customer."); await load();
      } else if (!e.data?.existingCustomerId) throw e;
    }
  });

  const decline = () => run(async () => {
    if (!window.confirm(`Decline ${app.legalName}?`)) return;
    await call("/decline", "POST", { internalNotes: notes });
    setMsg("Declined."); await load();
  });

  const invite = () => run(async () => {
    const r = await call("/send-invite", "POST");
    setMsg(`Portal invite sent to ${r.sentTo}.`);
  });

  if (!app) return <div className="w-full p-6">{error ? <p className="text-red-700">{error}</p> : "Loading…"}</div>;
  const decided = ["Approved", "Declined"].includes(app.status);
  const c = app.contacts || {};

  return (
    <div className="w-full p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/dashboard/applications" className="text-sm font-semibold text-slate-500 hover:text-slate-900">← Customers / Applications</Link>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">{app.legalName}</h1>
          <p className="text-sm text-slate-500">{app.reference} · submitted {new Date(app.createdAt).toLocaleString("en-NZ")}</p>
        </div>
        <span className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${statusStyle(app.status)}`}>{app.status}</span>
      </div>

      {error && <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {msg && <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{msg}</p>}

      <div className="grid gap-6 xl:grid-cols-[1.65fr_0.85fr]">
        <div className="space-y-6">
          <Card title="Business">
            <dl>
              <Row label="Entity type">{app.entityType}</Row>
              <Row label="Trading as">{app.tradingAs}</Row>
              <Row label="Company no. / NZBN">{[app.companyNumber, app.nzbn].filter(Boolean).join(" / ")}</Row>
              <Row label="Registered">{app.registrationDate}</Row>
              <Row label="Business type">{[app.businessType, app.businessTypeOther].filter(Boolean).join(" – ")}</Row>
              <Row label="Postal address">{addr(app.postalAddress)}</Row>
              <Row label="Physical address">{app.physicalSameAsPostal ? "Same as postal" : addr(app.physicalAddress)}</Row>
              <Row label="Phone">{app.phone}</Row>
              <Row label="Email">{app.email}</Row>
              <Row label="GST number">{app.notGstRegistered ? "Not GST registered" : app.gstNumber}</Row>
              <Row label="Credit requested">{app.creditLimitRequested != null ? `$${Number(app.creditLimitRequested).toLocaleString()}` : ""}</Row>
              <Row label="Est. monthly spend">{app.estMonthlySpend != null ? `$${Number(app.estMonthlySpend).toLocaleString()}` : ""}</Row>
              <Row label="Bank">{[app.bank?.name, app.bank?.branch].filter(Boolean).join(" – ")}</Row>
              <Row label="Accountant">{[app.accountant?.firm, app.accountant?.contact, app.accountant?.phone].filter(Boolean).join(" · ")}</Row>
            </dl>
          </Card>

          <Card title="Contacts & directors">
            <dl>
              <Row label="Purchasing">{contactLine(c.purchasing)}</Row>
              <Row label="Accounts">{contactLine(c.accounts)}</Row>
              <Row label="Accounts payable">{contactLine(c.accountsPayable)}</Row>
              <Row label="Invoice email">{app.invoiceEmail}</Row>
            </dl>
            {app.directors?.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm">
                {app.directors.map((d, i) => <li key={i}><span className="font-semibold">{d.name}</span> · {d.mobile} · {d.email}</li>)}
              </ul>
            )}
          </Card>

          <Card title="Trade references">
            {(app.tradeReferences || []).length === 0 ? <p className="text-sm text-slate-500">None supplied.</p> : (
              <ul className="space-y-3 text-sm">
                {app.tradeReferences.map((r, i) => (
                  <li key={i}><span className="font-semibold">{r.company}</span> — {r.contactName}<div className="text-slate-500">{[r.phone, r.email, r.address].filter(Boolean).join(" · ")}</div></li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Livestock profile">
            <dl>
              <Row label="NAIT number">{app.livestock?.naitNumber}</Row>
              <Row label="Farm name">{app.livestock?.farmName}</Row>
              <Row label="Island">{app.livestock?.island}</Row>
              <Row label="Stock types">{(app.livestock?.stockTypes || []).join(", ")}</Row>
              <Row label="Head per year">{app.livestock?.estHeadPerYear}</Row>
              <Row label="Stock agent">{app.livestock?.stockAgent}</Row>
            </dl>
          </Card>

          <Card title="Signature & declarations">
            <p className="text-sm">Signed by <strong>{app.signature?.name}</strong>, {app.signature?.title} on {app.signature?.signedAt ? new Date(app.signature.signedAt).toLocaleString("en-NZ") : ""}. Terms version {app.termsVersion}.</p>
            {app.signature?.image && <img src={app.signature.image} alt="Signature" className="mt-3 h-24 rounded-xl border border-slate-200 bg-white p-2" />}
            <p className="mt-3 text-xs text-slate-500">IP {app.signature?.ip} · {app.signature?.userAgent}</p>
            <ul className="mt-3 grid gap-1 text-sm sm:grid-cols-2">
              {Object.entries(app.consents || {}).map(([k, v]) => <li key={k}>{v ? "✓" : "✗"} {k}</li>)}
            </ul>
          </Card>
        </div>

        <div className="space-y-6">
          <div className="xl:sticky xl:top-6 space-y-6">
            <Card title="Decision">
              <label className="text-xs font-semibold uppercase text-slate-500">Credit limit approved ($)</label>
              <input type="number" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} className={`${INPUT} mt-1 mb-3`} disabled={decided} />
              <label className="text-xs font-semibold uppercase text-slate-500">Your title</label>
              <input value={approverTitle} onChange={(e) => setApproverTitle(e.target.value)} className={`${INPUT} mt-1 mb-3`} placeholder="e.g. Commercial Manager" disabled={decided} />
              <label className="text-xs font-semibold uppercase text-slate-500">Internal notes / reference checks</label>
              <textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} className={`${INPUT} mt-1`} disabled={app.status === "Approved"} />

              {!decided && (
                <>
                  <label className="mt-3 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={createDebtor} onChange={(e) => setCreateDebtor(e.target.checked)} /> Create MoneyWorks debtor (terms: due on receipt)
                  </label>
                  <div className="mt-4 grid gap-2">
                    <button disabled={busy} onClick={() => approve()} className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Approve</button>
                    <button disabled={busy} onClick={() => saveNotes("More info needed")} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">Mark “More info needed”</button>
                    <button disabled={busy} onClick={() => saveNotes()} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">Save notes</button>
                    <button disabled={busy} onClick={decline} className="rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">Decline</button>
                  </div>
                </>
              )}

              {app.status === "Approved" && (
                <div className="mt-4 space-y-2 text-sm">
                  <p className="text-slate-600">Approved by {app.approvedBy}{app.decisionDate ? ` on ${new Date(app.decisionDate).toLocaleDateString("en-NZ")}` : ""}.</p>
                  {app.customer && (
                    <button onClick={() => navigate(`/dashboard/customers/${app.customer._id || app.customer}`)} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 font-semibold hover:bg-slate-50">
                      Open customer{app.customer.moneyworksCode ? ` (${app.customer.moneyworksCode})` : ""}
                    </button>
                  )}
                  <button disabled={busy} onClick={invite} className="w-full rounded-xl bg-slate-900 px-4 py-2.5 font-semibold text-white hover:bg-slate-800 disabled:opacity-50">Send portal invite email</button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}